import { ChangeDetectionStrategy, Component, OnDestroy, Renderer2, computed, effect, inject, input, output, signal } from '@angular/core';
import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { TourService, TourSpotlight } from '../../../core/services/tour.service';
import { PlatformService } from '../../../core/services/platform.service';

export interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius: string;
  anchorTop?: number;
  anchorLeft?: number;
}

export interface MascotResponse {
  id: string;
  label: string;
  primary?: boolean;
  arrow?: boolean;
}

interface PlacementOptions {
  padding?: number;
  borderRadius?: string;
  circle?: boolean;
}

@Component({
  selector: 'app-mascot-bubble',
  imports: [NgOptimizedImage],
  templateUrl: './mascot-bubble.component.html',
  styleUrl: './mascot-bubble.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.ob-scrolling]': 'isScrolling()',
    '[class.ob-mac-electron]': 'platform.isMacElectron',
    '[class.mb-nudge]': 'onboarding.isNudge()',
  },
})
export class MascotBubbleComponent implements OnDestroy {
  protected onboarding = inject(TourService);
  protected readonly platform = inject(PlatformService);
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);

  // ── Inputs ────────────────────────────────────────────────────────────────

  /** Manual message override. If not provided, it will try to use the onboarding step message. */
  message = input<string>();
  /** List of response buttons. If not provided and in onboarding mode, it will show Next/Skip. */
  responses = input<readonly MascotResponse[]>();
  hint = input<string>();
  mascotImage = input<string>('/assets/knowzilla_default.png');
  mascotSize = input<number>(300);
  bubbleMaxWidth = input<string>('460px');

  /** When true, the component automatically syncs with OnboardingService. */
  onboardingMode = input<boolean>(false);

  /** External spotlight rect override. Used in PostSession feedback. */
  externalSpotlight = input<SpotlightRect | null>(null);
  showExternalOverlay = input<boolean>(false);

  // ── Outputs ───────────────────────────────────────────────────────────────

  responseClick = output<string>();

  // ── Internal State ────────────────────────────────────────────────────────

  leaving = signal(false);
  spotlight = signal<SpotlightRect | null>(null);
  private activeMessage = signal<string | null>(null);

  /** The cursor's own rect, when the step points at one control inside a larger spotlight. */
  private readonly cursorRect = signal<SpotlightRect | null>(null);
  /** True while the pointer is on the control the hand points at — see `watchCursorHover`. */
  readonly cursorHidden = signal(false);

  readonly isScrolling = signal(false);
  private scrollTimer: ReturnType<typeof setTimeout> | undefined;

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly isVisible = computed(() => {
    if (this.onboardingMode()) return this.onboarding.visible();
    return true; // Always visible if used manually
  });

  readonly isWelcome = computed(() => this.onboardingMode() && !!this.onboarding.currentStepData().welcome);

  readonly displayMessage = computed(() => {
    if (this.message()) return this.message()!;
    if (this.onboardingMode()) return this.activeMessage() ?? this.onboarding.currentStepData().message;
    return '';
  });

  readonly displayResponses = computed<readonly MascotResponse[]>(() => {
    if (this.responses()) return this.responses()!;
    if (this.onboardingMode()) {
      const res: MascotResponse[] = [];
      const step = this.onboarding.currentStepData();
      // A step that brought its own buttons replaces the pair outright: a nudge offering "Add
      // documents" and "Later" has no use for a Next that only closes it.
      if (step.responses?.length) return step.responses;

      const showNext = !step.tourTarget || !step.interactive;

      if (showNext) {
        let label = 'Next';
        if (this.isWelcome()) label = "Let's go";
        else if (this.onboarding.isLastStep()) label = 'Got it!';

        res.push({ id: 'next', label, primary: true, arrow: true });
      }

      if (this.isWelcome()) {
        res.push({ id: 'skip', label: 'Skip tour' });
      }
      return res;
    }
    return [];
  });

  readonly displayHint = computed(() => {
    if (this.hint()) return this.hint();
    if (this.onboardingMode() && this.isWelcome()) return 'You can always find the tutorial in your profile settings';
    return undefined;
  });

  /** Half the large indicator's fixed width — see `.mb-cursor-lg` for why centring is not a transform. */
  private static readonly LARGE_CURSOR_HALF_WIDTH = 50;

  /**
   * Under the zone and centred on it, rather than tucked into its bottom-right corner. On a nudge the
   * spotlight is a header control an inch tall, and a hand sitting in its corner reads as part of the
   * chrome; below it, at size, it reads as a hand.
   */
  readonly cursorPosition = computed(() => {
    const s = this.cursorRect() ?? this.activeSpotlight();
    if (!s) return null;

    if (this.onboarding.isNudge()) {
      return { top: s.top + s.height + 14, left: s.left + s.width / 2 - MascotBubbleComponent.LARGE_CURSOR_HALF_WIDTH };
    }
    return { top: s.top + s.height - 14, left: s.left + s.width - 40 };
  });

  readonly showCursorIndicator = computed(() => {
    if (!this.onboardingMode()) return false;
    const step = this.onboarding.currentStepData();
    return !!step.interactive && !!(step.cursorTarget ?? step.tourTarget);
  });

  readonly activeSpotlight = computed(() => this.externalSpotlight() ?? this.spotlight());
  readonly showOverlay = computed(() => {
    if (this.onboardingMode()) return true;
    return this.showExternalOverlay();
  });

  readonly blockerClipPath = computed(() => {
    const s = this.activeSpotlight();
    if (!s) return null;
    // The tour blocks everything and routes the one allowed click through its own interceptor. A
    // nudge cannot: the control it points at opens a menu on hover, which means real pointer events
    // have to reach it. So the blocker keeps a hole punched where the spotlight is — the page outside
    // is inert, the highlighted control behaves normally, and the dim finally means what it shows.
    if (this.onboardingMode() && !this.onboarding.isNudge()) return null;

    const { top, left, width, height } = s;
    const right = left + width;
    const bottom = top + height;

    // A polygon that covers the whole screen and has a rectangular hole
    return `polygon(0% 0%, 0% 100%, ${left}px 100%, ${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px 100%, 100% 100%, 100% 0%)`;
  });

  // ── Lifecycle & Logic ─────────────────────────────────────────────────────

  private cleanupClickHandler: (() => void) | null = null;
  private cleanupWaiter: (() => void) | null = null;
  private cleanupTracker: (() => void) | null = null;
  private cleanupCursorHover: (() => void) | null = null;
  private watchedCursorEl: HTMLElement | null = null;

  constructor() {
    effect(() => {
      // `ob-tour-active` locks body scroll. A nudge is not modal, so it does not take the page's
      // scroll away either.
      if (this.onboardingMode() && this.onboarding.visible() && !this.onboarding.isNudge()) {
        this.leaving.set(false);
        this.renderer.addClass(this.document.body, 'ob-tour-active');
      } else {
        this.renderer.removeClass(this.document.body, 'ob-tour-active');
      }
    });

    effect(() => {
      if (!this.onboardingMode()) return;

      this.cleanupClickHandler?.();
      this.cleanupClickHandler = null;
      this.cleanupWaiter?.();
      this.cleanupWaiter = null;
      this.cleanupTracker?.();
      this.cleanupTracker = null;
      this.unwatchCursorHover();
      this.cursorHidden.set(false);
      this.activeMessage.set(null);

      const target = this.onboarding.currentTourTarget();
      if (!target) {
        this.spotlight.set(null);
        return;
      }

      this.highlightTarget(target);
    });
  }

  // ── Highlight orchestration ───────────────────────────────────────────────

  private highlightTarget(target: TourSpotlight): void {
    if (this.tryPlace(target)) return;

    if (target.delayed) {
      this.waitForElement(target);
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tryPlace(target)) this.waitForElement(target);
    });
  }

  private tryPlace(target: TourSpotlight): boolean {
    if (Array.isArray(target.tourTarget)) {
      const elements = target.tourTarget.map(t => this.findElement(t)).filter((el): el is HTMLElement => el !== null);
      if (elements.length === 0) return false;
      void this.focusAndPlaceUnion(elements, target);
      return true;
    }

    const el = this.findElement(target.tourTarget);
    if (el) {
      void this.focusAndPlace(el, target);
      return true;
    }

    if (target.alternatives) {
      for (const alt of target.alternatives) {
        const altEl = this.findElement(alt.tourTarget);
        if (altEl) {
          void this.focusAndPlace(altEl, { padding: alt.padding, borderRadius: alt.borderRadius });
          this.activeMessage.set(alt.message);
          return true;
        }
      }
    }
    return false;
  }

  private waitForElement(target: TourSpotlight, timeoutMs = 5000): void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new MutationObserver(() => {
      if (this.onboardingMode() && !this.onboarding.visible()) {
        cleanup();
        return;
      }
      if (this.tryPlace(target)) cleanup();
    });
    const cleanup = () => {
      observer.disconnect();
      if (timer !== null) clearTimeout(timer);
      this.cleanupWaiter = null;
    };

    observer.observe(document.body, { childList: true, subtree: true });
    timer = setTimeout(cleanup, timeoutMs);
    this.cleanupWaiter = cleanup;
  }

  private async focusAndPlace(el: HTMLElement, target: TourSpotlight | PlacementOptions): Promise<void> {
    await this.scrollToIfNeeded(el);
    await this.waitForStablePosition(el);
    this.placeSpotlight(el, target);
    this.placeCursor();
    // The click goes where the hand points: a step can light a whole zone and still mean one control.
    // Not on a nudge: pressing the button opens a menu, and a step that ended there would take the
    // hint away at the exact moment the user still has a choice to make.
    if ('interactive' in target && target.interactive && !this.onboarding.isNudge()) {
      this.attachClickHandler(this.findClickElement() ?? el);
    }
    this.startTracking([el], () => {
      this.placeSpotlight(el, target);
      this.placeCursor();
    });
  }

  private async focusAndPlaceUnion(elements: HTMLElement[], target: TourSpotlight): Promise<void> {
    await this.scrollToIfNeeded(elements[0]);
    await this.waitForStablePosition(elements[0]);
    this.placeSpotlightUnion(elements, target);
    this.placeCursor();
    this.startTracking(elements, () => {
      this.placeSpotlightUnion(elements, target);
      this.placeCursor();
    });
  }

  private findCursorElement(): HTMLElement | null {
    const cursorTarget = this.onboarding.currentStepData().cursorTarget;
    return cursorTarget ? this.findElement(cursorTarget) : null;
  }

  private findClickElement(): HTMLElement | null {
    const step = this.onboarding.currentStepData();
    const clickTarget = step.clickTarget ?? step.cursorTarget;
    return clickTarget ? this.findElement(clickTarget) : null;
  }

  /** Measured separately from the spotlight, and in viewport coordinates — the indicator is fixed. */
  private placeCursor(): void {
    const el = this.findCursorElement();
    if (!el) {
      this.cursorRect.set(null);
      this.unwatchCursorHover();
      return;
    }

    const rect = el.getBoundingClientRect();
    this.cursorRect.set({ top: rect.top, left: rect.left, width: rect.width, height: rect.height, borderRadius: '0' });
    this.watchCursorHover(el);
  }

  /**
   * The hand stands under the control it points at, which is exactly where that control's menu opens.
   * Pointing at it is only useful until the pointer arrives, so it fades out on hover and comes back
   * when the pointer leaves — the menu gets the space, and a user who wanders off still gets the cue.
   */
  private watchCursorHover(el: HTMLElement): void {
    // Re-measuring can hand back a different element — the control was re-rendered under the hand.
    // Listeners then have to move with it, or they stay on a node no longer in the document and the
    // hand never fades again.
    if (this.watchedCursorEl === el) return;
    this.unwatchCursorHover();

    const hide = () => this.cursorHidden.set(true);
    const show = () => this.cursorHidden.set(false);

    el.addEventListener('mouseenter', hide);
    el.addEventListener('mouseleave', show);
    this.watchedCursorEl = el;
    this.cleanupCursorHover = () => {
      el.removeEventListener('mouseenter', hide);
      el.removeEventListener('mouseleave', show);
    };
  }

  private unwatchCursorHover(): void {
    this.cleanupCursorHover?.();
    this.cleanupCursorHover = null;
    this.watchedCursorEl = null;
  }

  private async waitForStablePosition(el: HTMLElement): Promise<void> {
    const animations: Animation[] = [];
    let current: HTMLElement | null = el;
    while (current) {
      animations.push(...current.getAnimations());
      if (current === document.body) break;
      current = current.parentElement;
    }
    if (animations.length === 0) return;
    await Promise.all(animations.map(a => a.finished.catch(() => undefined)));
  }

  private placeSpotlightUnion(elements: HTMLElement[], target: TourSpotlight): void {
    const rects = elements.map(el => el.getBoundingClientRect());
    const top = Math.min(...rects.map(r => r.top));
    const left = Math.min(...rects.map(r => r.left));
    const right = Math.max(...rects.map(r => r.right));
    const bottom = Math.max(...rects.map(r => r.bottom));
    const pad = target.padding ?? 10;

    this.spotlight.set({
      top: top - pad,
      left: left - pad,
      width: right - left + pad * 2,
      height: bottom - top + pad * 2,
      borderRadius: target.borderRadius ?? '14px',
    });
  }

  private startTracking(elements: HTMLElement[], update: () => void): void {
    let scheduled = false;
    const trigger = (event?: Event) => {
      if (event?.type === 'scroll') {
        if (!this.isScrolling()) this.isScrolling.set(true);
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          this.isScrolling.set(false);
        }, 100);
      }

      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (elements.every(el => el.isConnected)) update();
      });
    };

    document.addEventListener('scroll', trigger, { passive: true, capture: true });
    const resizeObserver = new ResizeObserver(() => trigger());
    elements.forEach(el => resizeObserver.observe(el));
    resizeObserver.observe(document.documentElement);

    this.cleanupTracker = () => {
      document.removeEventListener('scroll', trigger, { capture: true });
      resizeObserver.disconnect();
      clearTimeout(this.scrollTimer);
      this.isScrolling.set(false);
    };
  }

  private async scrollToIfNeeded(el: HTMLElement): Promise<void> {
    if (this.isFullyVisible(el)) return Promise.resolve();
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    return new Promise(resolve => setTimeout(resolve, 520));
  }

  private isFullyVisible(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();

    if (!this.fitsInBounds(rect, 0, 0, window.innerWidth, window.innerHeight)) {
      return false;
    }

    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const clips = style.overflowY !== 'visible' || style.overflowX !== 'visible';
      if (clips) {
        const pRect = parent.getBoundingClientRect();
        if (!this.fitsInBounds(rect, pRect.left, pRect.top, pRect.right, pRect.bottom)) {
          return false;
        }
      }
      parent = parent.parentElement;
    }
    return true;
  }

  private fitsInBounds(rect: DOMRect, bLeft: number, bTop: number, bRight: number, bBottom: number): boolean {
    const containerFitsElement = rect.height <= bBottom - bTop && rect.width <= bRight - bLeft;

    if (containerFitsElement) {
      return rect.top >= bTop && rect.bottom <= bBottom && rect.left >= bLeft && rect.right <= bRight;
    }
    return rect.bottom > bTop && rect.top < bBottom && rect.right > bLeft && rect.left < bRight;
  }

  private placeSpotlight(el: HTMLElement, opts: PlacementOptions): void {
    const rect = el.getBoundingClientRect();
    const pad = opts.padding ?? 10;

    if (opts.circle) {
      const size = Math.max(rect.width, rect.height) + pad * 2;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      this.spotlight.set({
        top: cy - size / 2,
        left: cx - size / 2,
        width: size,
        height: size,
        borderRadius: '50%',
      });
      return;
    }

    this.spotlight.set({
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      borderRadius: opts.borderRadius ?? '14px',
    });
  }

  private attachClickHandler(el: HTMLElement): void {
    const handler = () => this.onboarding.next();
    el.addEventListener('click', handler, { once: true });
    this.cleanupClickHandler = () => el.removeEventListener('click', handler);
  }

  private findElement(tourTarget: string): HTMLElement | null {
    const el = document.querySelector<HTMLElement>(`[data-tour="${tourTarget}"]`);
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0 ? el : null;
  }

  // ── Public handlers ───────────────────────────────────────────────────────

  onInterceptorClick(): void {
    if (!this.onboardingMode()) return;
    const stepData = this.onboarding.currentStepData();

    const clickTarget = stepData.clickTarget ?? stepData.cursorTarget ?? stepData.tourTarget;
    if (stepData.interactive && clickTarget) {
      this.document.querySelector<HTMLElement>(`[data-tour="${clickTarget}"]`)?.click();
    } else {
      this.onboarding.next();
    }
  }

  onMascotResponseClick(id: string): void {
    if (this.onboardingMode()) {
      if (id === 'next') this.onboarding.next();
      else if (id === 'skip') this.skipTour();
      else this.onboarding.respond(id);
    }
    this.responseClick.emit(id);
  }

  close(): void {
    this.leaving.set(true);
    this.cleanupClickHandler?.();
    this.cleanupClickHandler = null;
    this.cleanupWaiter?.();
    this.cleanupWaiter = null;
    this.cleanupTracker?.();
    this.cleanupTracker = null;
  }

  skipTour(): void {
    this.close();
    setTimeout(() => this.onboarding.complete(), 350);
  }

  ngOnDestroy(): void {
    this.unwatchCursorHover();
    this.cleanupClickHandler?.();
    this.cleanupWaiter?.();
    this.cleanupTracker?.();
    // `app.component.html` renders this component under `@if (tourVisible())`, so ending the tour
    // destroys it in the same change-detection pass that would have run the effect above. The
    // effect's else-branch never fires, and `ob-tour-active` — which locks body scroll — would be
    // left on the document for the rest of the session.
    this.renderer.removeClass(this.document.body, 'ob-tour-active');
  }
}
